import * as React from 'react'
import { EditorContent, Extension, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import UnderlineExtension from '@tiptap/extension-underline'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { marked } from 'marked'
import TurndownService from 'turndown'
import { Bold, Code, Heading1, Heading2, Heading3, Italic, Link2, List, ListOrdered, Minus, Quote, Redo, Strikethrough, Underline, Undo, Unlink } from 'lucide-react'
import { Button } from '~/client/components/ui/button'
import { Separator } from '~/client/components/ui/separator'
import { Toggle } from '~/client/components/ui/toggle'
import { cn } from '~/shared/common/utils'

type ToolbarItem = 'bold' | 'italic' | 'underline' | 'strikethrough' | 'code' | 'h1' | 'h2' | 'h3' | 'bulletList' | 'orderedList' | 'blockquote' | 'hr' | 'link' | 'unlink' | 'undo' | 'redo'

interface MinimalTiptapProps {
  initialContent?: string
  onChange?: (content: string) => void
  placeholder?: string
  editable?: boolean
  className?: string
  contentClassName?: string
  toolbarClassName?: string
  visibleTools?: ToolbarItem[]
  showToolbarOnFocusOnly?: boolean
  onChangeDelayMs?: number
}

function MinimalTiptap({ initialContent = '', onChange, placeholder = 'Start typing...', editable = true, className, contentClassName, toolbarClassName, visibleTools, showToolbarOnFocusOnly = false, onChangeDelayMs = 1500 }: MinimalTiptapProps) {
  const [isFocused, setIsFocused] = React.useState(false)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  // Configure markdown <-> HTML converters once
  const turndown = React.useMemo(() => {
    const td = new TurndownService({
      headingStyle: 'atx',
      hr: '---',
      bulletListMarker: '-',
      codeBlockStyle: 'fenced',
    })
    td.addRule('strikethrough', {
      filter: (node) => {
        const tag = node.nodeName.toUpperCase()
        return tag === 'S' || tag === 'STRIKE' || tag === 'DEL'
      },
      replacement: (content: string) => `~~${content}~~`,
    })
    td.addRule('underline', {
      filter: (node) => node.nodeName.toUpperCase() === 'U',
      replacement: (content: string) => `[${content}](underline:)`,
    })
    return td
  }, [])

  const markdownToHtml = React.useCallback((md: string): string => {
    if (!md) return ''
    marked.setOptions({ gfm: true, breaks: true })
    const html = marked.parse(md.replace(/\[([^\]]+)\]\(underline:\)/g, '<u>$1</u>'))
    return typeof html === 'string' ? html : ''
  }, [])

  const htmlToMarkdown = React.useCallback(
    (html: string): string => {
      if (!html) return ''
      return turndown.turndown(html)
    },
    [turndown]
  )

  // Debounce onChange callback to avoid excessive parent re-renders
  const onChangeTimeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      /*       Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
        defaultProtocol: 'https',
        HTMLAttributes: {
          class: 'underline text-blue-600 hover:text-blue-700 hover:underline',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }), */
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
        defaultProtocol: 'https',
        HTMLAttributes: {
          class: 'underline text-blue-600 hover:text-blue-700 hover:underline',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      UnderlineExtension,
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      // Blur on Escape — uses a raw ProseMirror plugin so it fires before StarterKit's default Escape binding
      Extension.create({
        name: 'blurOnEscape',
        addProseMirrorPlugins() {
          return [
            new Plugin({
              key: new PluginKey('blurOnEscape'),
              props: {
                handleKeyDown(view, event) {
                  if (event.key === 'Escape') {
                    view.dom.blur()
                    return true
                  }
                  return false
                },
              },
            }),
          ]
        },
      }),
    ],
    // Accept markdown in props, render as HTML inside the editor
    content: markdownToHtml(initialContent),
    editable,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      if (onChangeDelayMs <= 0) {
        onChange?.(htmlToMarkdown(html))
        return
      }

      // Debounce the onChange callback to avoid triggering parent re-renders on every keystroke
      if (onChangeTimeoutRef.current) {
        clearTimeout(onChangeTimeoutRef.current)
      }
      onChangeTimeoutRef.current = setTimeout(() => {
        onChangeTimeoutRef.current = undefined
        onChange?.(htmlToMarkdown(html))
      }, onChangeDelayMs) // Defaults to 1.5 seconds to reduce DB writes.
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => {
      setIsFocused(false)
      // Scroll to top when editor loses focus
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0
      }
    },
    editorProps: {
      attributes: {
        class: cn('tiptap prose prose-xs mx-auto focus:outline-none', 'min-h-[85px] pl-2 pr-2 border-0'),
      },
    },
  })

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (onChangeTimeoutRef.current) {
        clearTimeout(onChangeTimeoutRef.current)
      }
    }
  }, [])

  // Sync initialContent to editor when it changes externally
  React.useEffect(() => {
    if (!editor || isFocused || onChangeTimeoutRef.current) return

    const currentContent = htmlToMarkdown(editor.getHTML())
    if (currentContent !== initialContent) {
      editor.commands.setContent(markdownToHtml(initialContent))
    }
  }, [initialContent, editor, isFocused, htmlToMarkdown, markdownToHtml])

  if (!editor) {
    return null
  }

  const shouldShowTool = (tool: ToolbarItem): boolean => {
    if (!visibleTools) return true
    return visibleTools.includes(tool)
  }

  const showToolbar = !showToolbarOnFocusOnly || isFocused

  const setLink = () => {
    const linkAttributes = editor.getAttributes('link')
    const previousUrl = typeof linkAttributes.href === 'string' ? linkAttributes.href : ''
    const url = window.prompt('Set link URL', previousUrl ?? '')
    if (url === null) {
      return
    }
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  const canUnsetLink = (): boolean => {
    if (editor.isActive('link')) return true
    const { state } = editor
    const { from } = state.selection
    const linkType = editor.schema.marks.link
    if (!linkType) return false
    if (from <= 0) return false
    return state.doc.rangeHasMark(Math.max(0, from - 1), from, linkType)
  }

  const unsetLinkSmart = () => {
    const { state } = editor
    const { from } = state.selection
    const linkType = editor.schema.marks.link
    const isPrevLink = from > 0 && state.doc.rangeHasMark(Math.max(0, from - 1), from, linkType)
    const chain = editor.chain().focus()
    if (!editor.isActive('link') && isPrevLink) {
      chain.setTextSelection(from - 1)
    }
    chain.extendMarkRange('link').unsetLink().run()
  }

  return (
    <>
      <style>{`
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
        }
      `}</style>
      <div className={cn('border-l overflow-hidden -m-2 h-[calc(100%+16px)]', className)}>
        {showToolbar && (
        <div className={cn('border-b p-2 flex flex-wrap items-center gap-1', toolbarClassName)}>
          {shouldShowTool('bold') && (
            <Toggle
              type="button"
              size="sm"
              pressed={editor.isActive('bold')}
              onPressedChange={() => editor.chain().focus().toggleBold().run()}
              disabled={!editor.can().chain().focus().toggleBold().run()}
              aria-label="Bold"
            >
              <Bold className="h-4 w-4" />
            </Toggle>
          )}

          {shouldShowTool('italic') && (
            <Toggle
              type="button"
              size="sm"
              pressed={editor.isActive('italic')}
              onPressedChange={() => editor.chain().focus().toggleItalic().run()}
              disabled={!editor.can().chain().focus().toggleItalic().run()}
              aria-label="Italic"
            >
              <Italic className="h-4 w-4" />
            </Toggle>
          )}

          {shouldShowTool('underline') && (
            <Toggle
              type="button"
              size="sm"
              pressed={editor.isActive('underline')}
              onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
              disabled={!editor.can().chain().focus().toggleUnderline().run()}
              aria-label="Underline"
            >
              <Underline className="h-4 w-4" />
            </Toggle>
          )}

          {shouldShowTool('strikethrough') && (
            <Toggle
              type="button"
              size="sm"
              pressed={editor.isActive('strike')}
              onPressedChange={() => editor.chain().focus().toggleStrike().run()}
              disabled={!editor.can().chain().focus().toggleStrike().run()}
              aria-label="Strikethrough"
            >
              <Strikethrough className="h-4 w-4" />
            </Toggle>
          )}

          {shouldShowTool('code') && (
            <Toggle
              type="button"
              size="sm"
              pressed={editor.isActive('code')}
              onPressedChange={() => editor.chain().focus().toggleCode().run()}
              disabled={!editor.can().chain().focus().toggleCode().run()}
              aria-label="Code"
            >
              <Code className="h-4 w-4" />
            </Toggle>
          )}

          {(shouldShowTool('h1') || shouldShowTool('h2') || shouldShowTool('h3')) && <Separator orientation="vertical" className="h-6" />}

          {shouldShowTool('h1') && (
            <Toggle type="button" size="sm" pressed={editor.isActive('heading', { level: 1 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} aria-label="Heading 1">
              <Heading1 className="h-4 w-4" />
            </Toggle>
          )}

          {shouldShowTool('h2') && (
            <Toggle type="button" size="sm" pressed={editor.isActive('heading', { level: 2 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} aria-label="Heading 2">
              <Heading2 className="h-4 w-4" />
            </Toggle>
          )}

          {shouldShowTool('h3') && (
            <Toggle type="button" size="sm" pressed={editor.isActive('heading', { level: 3 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} aria-label="Heading 3">
              <Heading3 className="h-4 w-4" />
            </Toggle>
          )}

          {(shouldShowTool('bulletList') || shouldShowTool('orderedList') || shouldShowTool('blockquote')) && <Separator orientation="vertical" className="h-6" />}

          {shouldShowTool('bulletList') && (
            <Toggle type="button" size="sm" pressed={editor.isActive('bulletList')} onPressedChange={() => editor.chain().focus().toggleBulletList().run()} aria-label="Bullet list">
              <List className="h-4 w-4" />
            </Toggle>
          )}

          {shouldShowTool('orderedList') && (
            <Toggle type="button" size="sm" pressed={editor.isActive('orderedList')} onPressedChange={() => editor.chain().focus().toggleOrderedList().run()} aria-label="Ordered list">
              <ListOrdered className="h-4 w-4" />
            </Toggle>
          )}

          {shouldShowTool('blockquote') && (
            <Toggle type="button" size="sm" pressed={editor.isActive('blockquote')} onPressedChange={() => editor.chain().focus().toggleBlockquote().run()} aria-label="Block quote">
              <Quote className="h-4 w-4" />
            </Toggle>
          )}

          {shouldShowTool('hr') && (
            <>
              <Separator orientation="vertical" className="h-6" />
              <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().setHorizontalRule().run()} aria-label="Horizontal rule">
                <Minus className="h-4 w-4" />
              </Button>
            </>
          )}

          {(shouldShowTool('link') || shouldShowTool('unlink')) && (
            <>
              <Separator orientation="vertical" className="h-6" />
              {shouldShowTool('link') && (
                <Button type="button" variant="ghost" size="sm" onClick={setLink} disabled={!editor.can().chain().focus().setLink({ href: 'https://example.com' }).run()} aria-label="Set link">
                  <Link2 className="h-4 w-4" />
                </Button>
              )}
              {shouldShowTool('unlink') && (
                <Button type="button" variant="ghost" size="sm" onClick={unsetLinkSmart} disabled={!canUnsetLink()} aria-label="Remove link">
                  <Unlink className="h-4 w-4" />
                </Button>
              )}
            </>
          )}

          {(shouldShowTool('undo') || shouldShowTool('redo')) && <Separator orientation="vertical" className="h-6" />}

          {shouldShowTool('undo') && (
            <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().chain().focus().undo().run()} aria-label="Undo">
              <Undo className="h-4 w-4" />
            </Button>
          )}

          {shouldShowTool('redo') && (
            <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().chain().focus().redo().run()} aria-label="Redo">
              <Redo className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

        <EditorContent className={cn('max-h-[100px] overflow-y-auto rs-scrollbar-minimal', contentClassName)} editor={editor} ref={scrollContainerRef} />
      </div>
    </>
  )
}

export { MinimalTiptap, type MinimalTiptapProps, type ToolbarItem }
